






























































































































































import Vue from 'vue';
import MStoreTable from '@/components/shared/Table/MStoreTable.vue';
import MStoreSpinnerButton from '@/components/shared/MStoreSpinnerButton.vue';
import { CabinetConfigurationHelper, confirmationHelper } from '@/mixins';
import { BvTableFieldArray } from 'bootstrap-vue';
import { PermissionValue, SecurityConfiguration, SecurityEntity, SecurityEntityPermission, SecurityEntityType, UserModel, UserSearchModel } from '@/types';
import { SecurityConfigurationService } from '@/services/SecurityConfigurationService';
import LoadingSpinner from '@/components/shared/LoadingSpinner.vue';
import { UserService } from '@/services/UserService';
import { RoleService } from '@/services/RoleService';
import SnotifyConfiguration from '@/classes/SnotifyConfiguration';
import { RoleModel } from '@/services/GeneratedApi';

export default Vue.extend({
  name: 'SecurityConfiguration',
  mixins: [confirmationHelper, CabinetConfigurationHelper],
  props: {
    entityTypeId: {
      required: true,
      type: Number
    },
    entityPk: {
      required: true,
      type: String
    }
  },
  data() {
    return {
      isLoading: true,
      entityFilter: '',
      entityTypeFilter: -1,
      entityTypeFilters: [
        { value: -1, text: 'All' },
        { value: SecurityEntityType.User, text: 'User' },
        { value: SecurityEntityType.Role, text: 'Role' }
      ],
      entityCurrentPage: 1,
      permissionFilter: '',
      userFilter: '',
      roleFilter: '',
      itemToConfigure: {} as SecurityEntity,
      securityConfiguration: {} as SecurityConfiguration,
      filteredEntityPermissions: [] as SecurityEntityPermission[],
      allUsers: [] as UserModel[],
      allRoles: [] as RoleModel[],
      showUserModal: false,
      showRoleModal: false,
      isSavingPermissions: false,
      isLoadingUsers: false,
      isLoadingRoles: false
    };
  },
  computed: {
    permissionTableItems(this: any) {
      return (this.securityConfiguration as SecurityConfiguration).securityPermissions.map(({ id, description }) => ({
        id,
        description,
        access: (this.itemToConfigure as SecurityEntity).permissions?.find((itemPermission) => itemPermission.id === id)?.access ?? PermissionValue.NotSet
      }));
    },
    isItemBeingConfigured(this: any) {
      return Object.keys(this.itemToConfigure).length > 0;
    },
    tableItems(this: any) {
      const securityConfiguration: SecurityConfiguration = this.securityConfiguration;
      const securityEntities = securityConfiguration?.securityEntities;
      const filteredSecurityEntities = this.entityTypeFilter >= 0 ? securityEntities?.filter((securityEntity) => securityEntity.type === this.entityTypeFilter) : securityEntities;
      return (
        filteredSecurityEntities?.map(({ id, entityName, type, permissions }) => {
          return {
            id,
            entityName,
            type,
            permissions,
            permissionsSummary: `${permissions.filter(({ access }) => access === PermissionValue.Allow).length} Allowed, ${
              permissions.filter(({ access }) => access === PermissionValue.Deny).length
            } Denied, ${securityConfiguration.securityPermissions.length - permissions.length} Not set`
          };
        }) ?? []
      );
    }
  },
  methods: {
    updateAccess(this: any, permissionId: number, access: number) {
      const currentPermissions = [...this.itemToConfigure.permissions];
      const unchangedPermissions = currentPermissions.filter((x) => x.id !== permissionId);
      if (access === PermissionValue.NotSet) {
        Vue.set(this.itemToConfigure, 'permissions', [...unchangedPermissions]);
        return;
      }
      const permissionToAdd = { id: permissionId, access };
      Vue.set(this.itemToConfigure, 'permissions', [...unchangedPermissions, { ...permissionToAdd }]);
    },
    selectedRowClass(this: any, item: any, type: any): string | undefined {
      return type === 'row' && item?.entityName === (this.itemToConfigure as SecurityEntity)?.entityName ? 'table-primary' : '';
    },
    async onSecurityEntitySelected(this: any, selectedItem: SecurityEntity) {
      this.itemToConfigure = { ...selectedItem };
      this.filteredEntityPermissions = this.permissionTableItems;
      await Vue.nextTick();
    },
    setAllowAll(this: any) {
      const filteredPermissions = this.filteredEntityPermissions as SecurityEntityPermission[];
      const filteredPermissionIds = filteredPermissions.map((x) => x.id);
      const currentPermissions = [...(this.itemToConfigure as SecurityEntity).permissions];
      const unchangedPermissions = currentPermissions.filter((x) => !filteredPermissionIds.includes(x.id));
      const permissionsToAdd = filteredPermissions.map((x) => ({ ...x, access: PermissionValue.Allow }));
      Vue.set(this.itemToConfigure, 'permissions', [...unchangedPermissions, ...permissionsToAdd]);
    },
    unsetAll() {
      const filteredPermissions = this.filteredEntityPermissions as SecurityEntityPermission[];
      const filteredPermissionIds = filteredPermissions.map((x) => x.id);
      const currentPermissions = [...(this.itemToConfigure as SecurityEntity).permissions];
      const unchangedPermissions = currentPermissions.filter((x) => !filteredPermissionIds.includes(x.id));
      Vue.set(this.itemToConfigure, 'permissions', [...unchangedPermissions]);
    },
    setDenyAll() {
      const filteredPermissions = this.filteredEntityPermissions as SecurityEntityPermission[];
      const filteredPermissionIds = filteredPermissions.map((x) => x.id);
      const currentPermissions = [...(this.itemToConfigure as SecurityEntity).permissions];
      const unchangedPermissions = currentPermissions.filter((x) => !filteredPermissionIds.includes(x.id));
      const permissionsToAdd = filteredPermissions.map((x) => ({ ...x, access: PermissionValue.Deny }));
      Vue.set(this.itemToConfigure, 'permissions', [...unchangedPermissions, ...permissionsToAdd]);
    },
    async deleteEntity(this: any, securityEntity: SecurityEntity) {
      if (!(await this.hasDeleteConfirmedAsync(this.$bvModal))) {
        return;
      }
      await SecurityConfigurationService.UpdateSecurityConfiguration(this.entityTypeId, this.entityPk, { ...securityEntity, permissions: [] });
      SnotifyConfiguration.showSuccess(`${securityEntity.entityName} successfully removed`);
      const securityEntityToRemoveIndex = (this.securityConfiguration as SecurityConfiguration).securityEntities.findIndex((x) => x.type === securityEntity.type && x.id === securityEntity.id);
      Vue.delete(this.securityConfiguration.securityEntities, securityEntityToRemoveIndex);
    },
    cancelEntityPermissionsEdit(this: any) {
      this.itemToConfigure = {};
    },
    async addUser() {
      if (!this.allUsers?.length) {
        this.isLoadingUsers = true;
        try {
          this.allUsers = await UserService.getUsersAsync({} as UserSearchModel);
        } finally {
          this.isLoadingUsers = false;
        }
      }
      this.showUserModal = true;
    },
    async addRole() {
      if (!this.allRoles?.length) {
        this.isLoadingRoles = true;
        try {
          const rolesResponse = await RoleService.getRolesAsync();
          this.allRoles = rolesResponse.roles ?? [];
        } finally {
          this.isLoadingRoles = false;
        }
      }
      this.showRoleModal = true;
    },
    selectUserToAdd(this: any, user: UserModel) {
      const existingEntity = (this.securityConfiguration as SecurityConfiguration).securityEntities.find((x) => x.type === SecurityEntityType.User && x.id === user.id);
      let entityToConfigure: SecurityEntity;
      if (existingEntity) {
        entityToConfigure = existingEntity;
      } else {
        entityToConfigure = { id: user.id, entityName: user.username, type: SecurityEntityType.User, permissions: [] } as SecurityEntity;
        Vue.set((this.securityConfiguration as SecurityConfiguration).securityEntities, (this.securityConfiguration as SecurityConfiguration).securityEntities.length, entityToConfigure);
      }
      this.showUserModal = false;
      this.onSecurityEntitySelected(entityToConfigure);
    },
    selectRoleToAdd(this: any, role: RoleModel) {
      const existingEntity = (this.securityConfiguration as SecurityConfiguration).securityEntities.find((x) => x.type === SecurityEntityType.Role && x.id === role.id);
      let entityToConfigure: SecurityEntity;
      if (existingEntity) {
        entityToConfigure = existingEntity;
      } else {
        entityToConfigure = { id: role.id, entityName: role.name, type: SecurityEntityType.Role, permissions: [] } as SecurityEntity;
        Vue.set((this.securityConfiguration as SecurityConfiguration).securityEntities, (this.securityConfiguration as SecurityConfiguration).securityEntities.length, entityToConfigure);
      }
      this.showRoleModal = false;
      this.onSecurityEntitySelected(entityToConfigure);
    },
    async saveSecurityEntity() {
      this.isSavingPermissions = true;
      try {
        const securityEntityToUpdate = this.itemToConfigure as SecurityEntity;
        await SecurityConfigurationService.UpdateSecurityConfiguration(this.entityTypeId, this.entityPk, securityEntityToUpdate);
        const securityConfigurationEntityToUpdateIndex = (this.securityConfiguration as SecurityConfiguration).securityEntities.findIndex(
          (x) => x.id === securityEntityToUpdate.id && x.type === securityEntityToUpdate.type
        );
        Vue.set(this.securityConfiguration.securityEntities, securityConfigurationEntityToUpdateIndex, { ...securityEntityToUpdate });
        SnotifyConfiguration.showSuccess(`${securityEntityToUpdate.entityName} updated successfully`);
        this.itemToConfigure = {} as SecurityEntity;
      } finally {
        this.isSavingPermissions = false;
      }
    }
  },
  async mounted(this: any) {
    await this.getLock();
    this.securityConfiguration = (await SecurityConfigurationService.GetSecurityConfiguration(this.entityTypeId, this.entityPk)) as SecurityConfiguration;
    this.isLoading = false;
  },
  created(this: any) {
    this.SECURITY_ENTITIES_FIELDS = [
      { key: 'typeIcon', label: '', class: 'action-element' },
      { key: 'entityName', label: 'Role/User' },
      { key: 'permissionsSummary', label: 'Permissions' },
      { key: 'actions', class: 'action-element', label: '' }
    ] as BvTableFieldArray;
    this.PERMISSIONS_FIELDS = [
      { key: 'description', label: 'Description' },
      { key: 'access', label: 'Access', class: 'action-element' }
    ] as BvTableFieldArray;
    this.USERS_FIELDS = [
      { key: 'username', label: 'Username' },
      { key: 'emailAddress', label: 'Email' },
      { key: 'actions', label: 'Actions', class: 'action-element' }
    ] as BvTableFieldArray;
    this.ROLES_FIELDS = [
      { key: 'name', label: 'Name' },
      { key: 'actions', label: 'Actions', class: 'action-element' }
    ] as BvTableFieldArray;
    this.ACCESS_OPTIONS = [
      { text: 'Allow', value: PermissionValue.Allow },
      { text: 'Not Set', value: PermissionValue.NotSet },
      { text: 'Deny', value: PermissionValue.Deny }
    ];
    this.COMMON_SEARCH_TERMS = ['View', 'Configure', 'Delete'];
  },
  components: {
    MStoreTable,
    MStoreSpinnerButton,
    LoadingSpinner
  }
});
